/** @module IglooLogger **/

/**
 * Export the IglooLogger Module
 * @param  {Igloo}  igloo The global igloo module
 * @param  {String} name  The name associated with this logger. This will be
 *                        printed with every log message
 * @return {Object}       Public functions:
 * [trace]{@link module:IglooLogger~trace},
 * [verbose]{@link module:IglooLogger~verbose},
 * [info]{@link module:IglooLogger~info},
 * [warn]{@link module:IglooLogger~warn}, and
 * [error]{@link module:IglooLogger~error}
 */
module.exports = function(igloo, name) {
  'use strict';

  /**
   * Boolean that determines whether to style the name at the start of the
   * console log. This should detect IE 10 and below which don't support %c in
   * console logs
   * @type {Boolean}
   */
  var do_style = ['IE', 'Edge'].indexOf(igloo.browser.name) === -1;

  /**
   * Log the passed arguments preceded by a stylized version of the name of the
   * logger
   * @param  {String} style_string The style to use for the logger name
   * @param  {Array} args          The arguments to pass to the console
   * @param  {Function} method     The console logging function to use
   */
  function log(style_string, args, method) {
    // Default the method to console.log
    if (typeof method !== 'function') {
      method = console.log;
    }

    // Calculate the timing
    var time_diff = (((new Date()).getTime() - igloo.started) / 1000).toFixed(3);
    if (time_diff.length < 7) {
      time_diff = ('0000000' + time_diff).substring(time_diff.length);
    }

    // Add the styling
    var name_string = time_diff + ' ' + name;
    if (do_style) {
      args.splice(0, 0, '%c' + name_string, style_string);
    } else {
      args.splice(0, 0, name_string);
    }

    // Log the results
    return method.apply(console, args);
  } // --> log

  /**
   * Log the information with styling if verbose debugging is enabled
   * @param  {String} name The name of the function
   * @param  {Object} args The `arguments` variable from the function
   */
  function trace(name, args) {
    // Check for verbose debugging
    if (igloo.debug !== 'v' && igloo.debug !== 'vv') {
      return true;
    }

    // Add the trace string
    args = [name, Array.prototype.slice.call(args)];
    args.unshift('TRACE');

    // Log the results
    return log(
      'color:purple;font-size:medium',
      args,
      console.debug
    );
  } // --> trace

  /**
   * Log the information with styling if verbose debugging is enabled
   */
  function verbose() {
    // Check for verbose debugging
    if (igloo.debug !== 'v' && igloo.debug !== 'vv') {
      return true;
    }

    // Log the results
    return log(
      'font-size:medium;',
      Array.prototype.slice.call(arguments),
      console.debug
    );
  } // --> verbose

  /**
   * Log the information with a blue stylized name if debugging is enabled
   */
  function info() {
    // Check for debugging to be enabled
    if (igloo.debug !== true && igloo.debug !== 'v' && igloo.debug !== 'vv') {
      return true;
    }

    // Log the results
    return log(
      'color:blue;font-size:medium;',
      Array.prototype.slice.call(arguments),
      console.info
    );
  } // --> info

  /**
   * Log the information with an orange stylized name if debugging is enabled
   */
  function warn() {
    // Check for debugging to be enabled
    if (igloo.debug !== true && igloo.debug !== 'v' && igloo.debug !== 'vv') {
      return true;
    }

    // Log the results
    return log(
      'color:orange;font-size:medium;',
      Array.prototype.slice.call(arguments),
      console.warn
    );
  } // --> warn

  /**
   * Log the information with a red stylized name if debugging is enabled
   */
  function error() {
    // Log the results
    return log(
      'color:red;font-size:medium;',
      Array.prototype.slice.call(arguments),
      console.error
    );
  } // --> error

  return {
    trace: trace,
    verbose: verbose,
    info: info,
    warn: warn,
    error: error,
  };
}; // --> module.exports
